import React, { useEffect, useState, useMemo, useCallback } from 'react';
import UiSelect from '../components/UiSelect.jsx';
import { createPortal } from 'react-dom';
import { getRooms, getSeatsData, getLibraries } from '../services/api.js';
import { getStudentFormCache } from './StudentEditorModal.jsx';
import { getSeatsCache, isSeatsCacheFresh, setSeatsCache } from '../utils/seatsCache.js';
import { getTenantSessionScope } from '../api/api1.js';

let seatCache = {
  seats: null,
  rooms: null,
  libraries: null,
  ts: 0,
  scope: '',
};
const CACHE_TTL = 5 * 60 * 1000;

function formatSeatNumber(value){
  if(value===undefined || value===null) return '';
  const s=String(value).trim(); if(!s) return '';
  const m=s.match(/\d+/g); if(m) return m.join('-');
  return s.replace(/[^0-9-]/g,'').trim() || s;
}

function seatStatus(seat){
  if(seat?.student_id || seat?.studentid) return 'occupied';
  const raw=String(seat?.status||'').toLowerCase();
  if(raw.includes('occupied')||raw.includes('booked')||raw.includes('taken')) return 'occupied';
  if(raw.includes('reserved')||raw.includes('block')) return 'reserved';
  return 'available';
}

function seatCategory(seat){
  const raw=String(seat?.category||seat?.seat_category||'').toLowerCase();
  return raw.includes('non')?'nonac':'ac';
}

export default function SeatSelectionModal({ isOpen, onClose, onSeatSelected, studentName, preSelectedLibrary }){
  const [seats,setSeats]=useState([]); 
  const [rooms,setRooms]=useState([]); 
  const [libraries,setLibraries]=useState([]);
  const [loading,setLoading]=useState(true); 
  const [error,setError]=useState(null);
  const [selectedCategory,setSelectedCategory]=useState('all');
  const [selectedStatus,setSelectedStatus]=useState('available');
  const [selectedRoom,setSelectedRoom]=useState('all');
  const [selectedLibrary,setSelectedLibrary]=useState('all');
  const [searchQuery,setSearchQuery]=useState('');
  const [assignmentMode,setAssignmentMode]=useState('occupied');
  const [filtersOpen,setFiltersOpen]=useState(false);

const loadData=useCallback(async(force=false)=>{
    const sessionScope = getTenantSessionScope();
    if (seatCache.scope !== sessionScope) {
      seatCache = { seats: null, rooms: null, libraries: null, ts: 0, scope: sessionScope };
    }
    setLoading(true); setError(null);
    try{
      const now = Date.now();
      const shared = getSeatsCache();
      if(!force && isSeatsCacheFresh()){
        const seatsData = shared.seats || [];
        const roomsData = shared.rooms || [];
        const libsData = shared.libraries || [];
        seatCache = { seats: seatsData, rooms: roomsData, libraries: libsData, ts: Date.now(), scope: sessionScope };
        setSeats(seatsData);
        setRooms(roomsData);
        setLibraries(libsData);
        setLoading(false);
        return;
      }
      if(!force && seatCache.seats && seatCache.rooms && seatCache.libraries && now - seatCache.ts < CACHE_TTL){
        setSeats(seatCache.seats);
        setRooms(seatCache.rooms);
        setLibraries(seatCache.libraries);
        setLoading(false);
        return;
      }
      const formCache = getStudentFormCache();
      if(!force && formCache.seats && formCache.libraries && now - formCache.ts < CACHE_TTL){
        const sRes = formCache.seats;
        const lRes = formCache.libraries;
        const rRes = await getRooms().catch(()=>[]);
        if (sessionScope !== getTenantSessionScope()) return;
        const seatsData = Array.isArray(sRes) ? sRes : (sRes?.data || sRes?.seats || sRes?.result || []);
        const roomsData = Array.isArray(rRes) ? rRes : (rRes?.data || rRes?.rooms || rRes?.result || []);
        const libsData = Array.isArray(lRes) ? lRes : (lRes?.data || lRes?.libraries || lRes?.result || []);
        seatCache = { seats: seatsData, rooms: roomsData, libraries: libsData, ts: Date.now(), scope: sessionScope };
        setSeatsCache({ seats: seatsData, rooms: roomsData, libraries: libsData }, sessionScope);
        setSeats(seatsData);
        setRooms(roomsData);
        setLibraries(libsData);
        setLoading(false);
        return;
      }
      const [sRes,rRes,lRes]=await Promise.all([
        getSeatsData(force),
        getRooms(),
        getLibraries()
      ]);
      if (sessionScope !== getTenantSessionScope()) return;

      // Support all response shapes: [] or {data:[]} or {seats:[]}
      const seatsData = Array.isArray(sRes) ? sRes : (sRes?.data || sRes?.seats || sRes?.result || []);
      const roomsData = Array.isArray(rRes) ? rRes : (rRes?.data || rRes?.rooms || rRes?.result || []);
      const libsData = Array.isArray(lRes) ? lRes : (lRes?.data || lRes?.libraries || lRes?.result || []);

      console.log('Seats:', seatsData.length, 'Rooms:', roomsData.length, 'Libs:', libsData.length);
      seatCache = { seats: seatsData, rooms: roomsData, libraries: libsData, ts: Date.now(), scope: sessionScope };
      setSeatsCache({ seats: seatsData, rooms: roomsData, libraries: libsData }, sessionScope);

      setSeats(seatsData);
      setRooms(roomsData);
      setLibraries(libsData);
    }catch(e){
      if (sessionScope !== getTenantSessionScope()) return;
      console.error('Load error', e);
      setError('Failed to load seats');
      setSeats([]); setRooms([]); setLibraries([]);
    }finally{
      setLoading(false);
    }
  },[]);

  useEffect(()=>{ if(isOpen) loadData(); },[isOpen,loadData]);

  // Keep the filter panel collapsed each time the modal opens (mobile-first).
  useEffect(()=>{ if(!isOpen) setFiltersOpen(false); },[isOpen]);

  // Close on Escape and lock background scrolling while the modal is open.
  useEffect(()=>{
    if(!isOpen) return;
    const onKey=(e)=>{
      if(e.key!=='Escape') return;
      // Let an open dropdown consume Escape first.
      if(document.querySelector('.form-select-custom.open')) return;
      onClose?.();
    };
    document.addEventListener('keydown',onKey);
    const prevOverflow=document.body.style.overflow;
    document.body.style.overflow='hidden';
    return ()=>{
      document.removeEventListener('keydown',onKey);
      document.body.style.overflow=prevOverflow;
    };
  },[isOpen,onClose]);
  useEffect(()=>{
    if(!isOpen) return;
    if(!preSelectedLibrary){ setSelectedLibrary('all'); return; }
    const wanted=String(preSelectedLibrary);
    const match=libraries.find(l=>{
      const id=String(l.id ?? l.library_id ?? '');
      const name=String(l.library_name||l.name||'').toLowerCase();
      return id===wanted || name===wanted.toLowerCase();
    });
    if(!match){ setSelectedLibrary('all'); return; }
    const libraryId=String(match.id ?? match.library_id);
    const hasSeats=seats.some(seat=>{
      const room=rooms.find(r=>String(r.room_id||r.id)===String(seat.room_id));
      return room && String(room.library_id||room.libraryId||room.libraryid)===libraryId;
    });
    setSelectedLibrary(hasSeats?libraryId:'all');
  },[preSelectedLibrary,isOpen,libraries,seats,rooms]);

  const roomMap=useMemo(()=>{ const m={}; rooms.forEach(r=>{ const id=r.room_id||r.id; m[id]=r.room_name||r.name||`Room ${id}`; }); return m; },[rooms]);

  const uniqueRooms=useMemo(()=>{
    let ids=[...new Set(seats.map(s=>s.room_id).filter(Boolean))];
    if(selectedLibrary!=='all'){
      ids=ids.filter(id=>{
        const rm=rooms.find(r=>String(r.room_id||r.id)===String(id));
        return rm && String(rm.library_id||rm.libraryId||rm.libraryid)===String(selectedLibrary);
      });
    }
    return ids.sort((a,b)=>(roomMap[a]||'').localeCompare(roomMap[b]||''));
  },[seats,rooms,roomMap,selectedLibrary]);

  // Seats matching context filters only (room/library/search). Used for the
  // stat chips so their counts stay meaningful while status/category are toggled.
  const baseSeats=useMemo(()=>seats.filter(seat=>{
    const roomMatch=selectedRoom==='all'||String(seat.room_id)===String(selectedRoom);
    let libMatch=true;
    if(selectedLibrary!=='all'){
      const rm=rooms.find(r=>String(r.room_id||r.id)===String(seat.room_id));
      if(!rm || String(rm.library_id||rm.libraryId||rm.libraryid)!==String(selectedLibrary)) libMatch=false;
    }
    const sNum=(seat.seat_number||seat.seatno||seat.seat_no||'').toString().toLowerCase();
    const rName=(roomMap[seat.room_id]||'').toLowerCase();
    const searchMatch=!searchQuery||sNum.includes(searchQuery.toLowerCase())||rName.includes(searchQuery.toLowerCase());
    return roomMatch&&libMatch&&searchMatch;
  }),[seats,selectedRoom,selectedLibrary,searchQuery,roomMap,rooms]);

  const filteredSeats=useMemo(()=>baseSeats.filter(seat=>{
    const catMatch=selectedCategory==='all'||seatCategory(seat)===selectedCategory;
    const statusMatch=selectedStatus==='all'||seatStatus(seat)===selectedStatus;
    return catMatch&&statusMatch;
  }),[baseSeats,selectedCategory,selectedStatus]);

  const seatsByRoom=useMemo(()=>filteredSeats.reduce((acc,seat)=>{
    const rid=seat.room_id||'unassigned';
    if(!acc[rid]) acc[rid]={ac:[],nonac:[],roomName:roomMap[rid]||'Unassigned'};
    acc[rid][seatCategory(seat)].push(seat); return acc;
  },{}),[filteredSeats,roomMap]);

  const stats=useMemo(()=>{
    const av=baseSeats.filter(s=>seatStatus(s)==='available').length;
    const oc=baseSeats.filter(s=>seatStatus(s)==='occupied').length;
    const re=baseSeats.filter(s=>seatStatus(s)==='reserved').length;
    const ac=baseSeats.filter(s=>seatCategory(s)==='ac').length;
    return {total:baseSeats.length, reserved:re, available:av, occupied:oc, ac, nonac:baseSeats.length-ac};
  },[baseSeats]);

  const activeFilterCount=useMemo(()=>{
    let count=0;
    if(selectedCategory!=='all') count++;
    if(selectedStatus!=='available') count++;
    if(selectedRoom!=='all') count++;
    if(selectedLibrary!=='all') count++;
    return count;
  },[selectedCategory,selectedStatus,selectedRoom,selectedLibrary]);

  const handleSeatClick=(seat)=>{
    if(seatStatus(seat)==='occupied') return;
    const room=rooms.find(r=>String(r.room_id||r.id)===String(seat.room_id));
    onSeatSelected({
      ...seat, id:seat.seat_id||seat.id,
      seatnumber:formatSeatNumber(seat.seat_number||seat.seatno),
      room_name:roomMap[seat.room_id],
      library_id:room?.library_id||room?.libraryId||room?.libraryid,
      selection_type:assignmentMode
    });
    onClose();
  };

  const reset=()=>{ setSelectedCategory('all'); setSelectedStatus('available'); setSelectedRoom('all'); setSelectedLibrary('all'); setSearchQuery(''); };

  if(!isOpen) return null;

  return createPortal(
    <div className="seat-modal-overlay" onClick={onClose}>
      <div className="seat-modal-container" onClick={e=>e.stopPropagation()}>
        <div className="seat-modal-header">
          <h3 className="seat-modal-title">Select Seat {studentName && <span className="seat-modal-student">for {studentName}</span>}</h3>
          <button className="seat-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="seat-search-container">
          <div className={`seat-search-field ${searchQuery?'has-value':''}`}>
            <input
              className="seat-search-input"
              type="text"
              value={searchQuery}
              onChange={e=>setSearchQuery(e.target.value)}
              placeholder="Search by seat number or room name..."
              enterKeyHint="search"
              aria-label="Search seats"
            />
            {searchQuery && <button type="button" className="seat-search-clear" onClick={()=>setSearchQuery('')} aria-label="Clear search">×</button>}
          </div>
        </div>

        <div className={`filters-shell ${filtersOpen?'open':''}`}>
          <button
            type="button"
            className="filters-toggle"
            onClick={()=>setFiltersOpen(open=>!open)}
            aria-expanded={filtersOpen}
            aria-controls="seat-filters-panel"
          >
            <span className="filters-toggle-title">Filters</span>
            <span className={`filters-mode ${assignmentMode}`}>{assignmentMode==='occupied'?'Occupied':'Reserved'}</span>
            {activeFilterCount>0 && <span className="filters-count">{activeFilterCount}</span>}
            <span className="filters-caret" aria-hidden="true" />
          </button>
          <div className="filters-row" id="seat-filters-panel">
            <div className="f-group"><label>ASSIGN AS</label><div className="assign-toggle"><button type="button" className={assignmentMode==='occupied'?'active-general':''} onClick={()=>setAssignmentMode('occupied')} aria-pressed={assignmentMode==='occupied'}>Occupied</button><button type="button" className={assignmentMode==='reserved'?'active-reserved':''} onClick={()=>setAssignmentMode('reserved')} aria-pressed={assignmentMode==='reserved'}>Reserved</button></div></div>
            <div className="f-group"><label>CATEGORY</label><UiSelect value={selectedCategory} onChange={e=>setSelectedCategory(e.target.value)}><option value="all">All Categories</option><option value="ac">AC</option><option value="nonac">Non-AC</option></UiSelect></div>
            <div className="f-group"><label>LIBRARY</label><UiSelect value={selectedLibrary} onChange={e=>{setSelectedLibrary(e.target.value); setSelectedRoom('all');}}><option value="all">All Libraries</option>{libraries.map(l=><option key={l.id||l.library_id} value={l.id||l.library_id}>{l.library_name||l.name}</option>)}</UiSelect></div>
            <div className="f-group"><label>ROOM</label><UiSelect value={selectedRoom} onChange={e=>setSelectedRoom(e.target.value)}><option value="all">All Rooms</option>{uniqueRooms.map(id=><option key={id} value={id}>{roomMap[id]}</option>)}</UiSelect></div>
            <div className="f-group"><label>STATUS</label><UiSelect value={selectedStatus} onChange={e=>setSelectedStatus(e.target.value)}><option value="all">All Status</option><option value="available">Available Only</option><option value="occupied">Occupied Only</option><option value="reserved">Reserved Only</option></UiSelect></div>
            <div className="f-group"><label>&nbsp;</label><button type="button" className="reset-btn" onClick={reset}>Reset</button></div>
          </div>
        </div>

        <div className="stats-bar" role="group" aria-label="Filter seats by availability or category">
          <button type="button" className={`stat-total ${selectedStatus==='all'&&selectedCategory==='all'?'active':''}`} onClick={()=>{setSelectedStatus('all');setSelectedCategory('all');}} aria-pressed={selectedStatus==='all'&&selectedCategory==='all'}>Total <b>{stats.total}</b></button><span className="sep">|</span>
          <button type="button" className={`stat-reserved ${selectedStatus==='reserved'?'active':''}`} onClick={()=>setSelectedStatus(prev=>prev==='reserved'?'available':'reserved')} aria-pressed={selectedStatus==='reserved'}>Reserved <b>{stats.reserved}</b></button><span className="sep">|</span>
          <button type="button" className={`stat-available ${selectedStatus==='available'?'active':''}`} onClick={()=>setSelectedStatus(prev=>prev==='available'?'all':'available')} aria-pressed={selectedStatus==='available'}>Available <b>{stats.available}</b></button><span className="sep">|</span>
          <button type="button" className={`stat-occupied ${selectedStatus==='occupied'?'active':''}`} onClick={()=>setSelectedStatus(prev=>prev==='occupied'?'available':'occupied')} aria-pressed={selectedStatus==='occupied'}>Occupied <b>{stats.occupied}</b></button><span className="sep">|</span>
          <button type="button" className={`stat-ac ${selectedCategory==='ac'?'active':''}`} onClick={()=>setSelectedCategory(prev=>prev==='ac'?'all':'ac')} aria-pressed={selectedCategory==='ac'}>AC <b>{stats.ac}</b></button><span className="sep">|</span>
          <button type="button" className={`stat-nonac ${selectedCategory==='nonac'?'active':''}`} onClick={()=>setSelectedCategory(prev=>prev==='nonac'?'all':'nonac')} aria-pressed={selectedCategory==='nonac'}>Non-AC <b>{stats.nonac}</b></button>
        </div>

        <div className="seat-grid-wrap">
          {loading ? <div style={{padding:'60px', textAlign:'center'}}>Loading seats...</div> :
           error ? <div style={{padding:'60px', textAlign:'center', color:'#ef4444'}}>{error} <br/><button onClick={()=>loadData(true)} style={{marginTop:'10px', color:'#6366f1', textDecoration:'underline', border:'none', background:'none'}}>Retry</button></div> :
           Object.entries(seatsByRoom).length===0 ? <div style={{padding:'48px 16px', textAlign:'center', color:'#475569'}}>No seats found<br/><small style={{color:'#94a3b8'}}>{seats.length} total loaded but filtered out</small></div> :
           Object.entries(seatsByRoom).map(([rid,data])=>(
            <div key={rid} className="room-block">
              <div className="room-head"><b>{data.roomName}</b><span className="badge">{data.ac.length+data.nonac.length} seats</span></div>
              {data.ac.length>0 && <>
                <div className="sec-title"><span className="section-mark ac-mark" aria-hidden="true"/>AC seats <span>({data.ac.length})</span></div>
                <div className="seats-grid">{data.ac.map(seat=><button key={seat.seat_id||seat.id} type="button" onClick={()=>handleSeatClick(seat)} className={`seat-box ${seatStatus(seat)}`} disabled={seatStatus(seat)!=='available'}><b>{formatSeatNumber(seat.seat_number||seat.seatno)}</b><small>AC</small></button>)}</div>
              </>}
              {data.ac.length>0 && data.nonac.length>0 && <div className="divider"/>}
              {data.nonac.length>0 && <>
                <div className="sec-title"><span className="section-mark nonac-mark" aria-hidden="true"/>Non-AC seats <span>({data.nonac.length})</span></div>
                <div className="seats-grid">{data.nonac.map(seat=><button key={seat.seat_id||seat.id} type="button" onClick={()=>handleSeatClick(seat)} className={`seat-box ${seatStatus(seat)}`} disabled={seatStatus(seat)!=='available'}><b>{formatSeatNumber(seat.seat_number||seat.seatno)}</b><small>NON-AC</small></button>)}</div>
              </>}
            </div>
           ))}
        </div>

        <div className="modal-foot"><button type="button" onClick={onClose}>Cancel</button></div>
      </div>

      <style>{`
        .seat-modal-overlay{position:fixed;inset:0;background:rgba(15,23,42,.62);z-index:11000;display:flex;align-items:center;justify-content:center;padding:16px;backdrop-filter:blur(5px);overflow:auto;overscroll-behavior:contain}
        .seat-modal-container{width:min(1200px,100%);height:min(92vh,900px);height:min(92dvh,900px);max-height:calc(100dvh - 32px);min-height:0;background:#fff;border-radius:16px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 25px 70px rgba(0,0,0,.3)}
        .seat-modal-header{flex:0 0 auto;padding:16px 20px;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;gap:12px;background:linear-gradient(110deg,#f8fafc,#eef2ff)}
        .seat-modal-title{margin:0;min-width:0;font-size:20px;line-height:1.25;font-weight:700;color:#1e293b}
        .seat-modal-student{font-weight:400;font-size:14px;color:#64748b}
        .seat-close{flex:0 0 auto;width:38px;height:38px;border:1px solid #e2e8f0;border-radius:10px;background:#fff;font-size:24px;line-height:1;cursor:pointer;color:#64748b}
        .seat-search-container{flex:0 0 auto;padding:12px 20px;border-bottom:1px solid #e5e7eb}
        .seat-search-field{position:relative;display:flex;align-items:center}
        .seat-search-input{display:block;width:100%;min-width:0;padding:10px 14px;border:1px solid #e5e7eb;border-radius:10px;font-size:13px;outline:none;background:#fff;color:#0f172a}
        .seat-search-field.has-value .seat-search-input{padding-right:40px}
        .seat-search-input::placeholder{color:#94a3b8}
        .seat-search-input:focus{border-color:#818cf8;box-shadow:0 0 0 3px rgba(99,102,241,.12)}
        .seat-search-clear{position:absolute;right:7px;top:50%;transform:translateY(-50%);display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;padding:0;border:none;border-radius:50%;background:#eef2f7;color:#475569;font-size:17px;line-height:1;cursor:pointer}
        .seat-search-clear:hover{background:#e2e8f0;color:#1e293b}
        .filters-shell{flex:0 0 auto;border-bottom:1px solid #e5e7eb;background:#fff}
        .filters-toggle{display:none}
        .filters-toggle-title{white-space:nowrap}
        .filters-mode{display:inline-flex;align-items:center;padding:2px 7px;border-radius:999px;font-size:10px;font-weight:700}
        .filters-mode.occupied{background:#fee2e2;color:#b91c1c}
        .filters-mode.reserved{background:#fef3c7;color:#b45309}
        .filters-count{display:inline-flex;align-items:center;justify-content:center;min-width:17px;height:17px;padding:0 5px;border-radius:999px;background:#6366f1;color:#fff;font-size:10px;font-weight:700}
        .filters-caret{margin-left:auto;flex:0 0 auto;width:8px;height:8px;border-right:2px solid #64748b;border-bottom:2px solid #64748b;transform:translateY(-2px) rotate(45deg);transition:transform .18s ease}
        .filters-shell.open .filters-caret{transform:translateY(2px) rotate(-135deg)}
        .filters-row{padding:12px 20px;display:grid;grid-template-columns:150px repeat(4,minmax(0,1fr)) 80px;gap:10px;align-items:end}
        .f-group{display:flex;flex-direction:column;min-width:0} .f-group label{font-size:10px;font-weight:700;color:#6b7280;margin-bottom:4px;letter-spacing:.5px}
        .f-group .form-select-custom-trigger{min-height:34px;width:100%;min-width:0;max-width:100%;padding:8px 10px;border:1px solid #e5e7eb;border-radius:8px;background:#f8fafc;font-size:12px}
        .assign-toggle{display:flex;background:#f1f5f9;border-radius:8px;padding:2px;gap:2px} .assign-toggle button{flex:1;border:none;padding:6px 10px;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;background:transparent;color:#64748b}
        .assign-toggle .active-general{background:#ef4444;color:#fff} .assign-toggle .active-reserved{background:#f59e0b;color:#fff}
        .reset-btn{padding:8px 12px;border:1px solid #e5e7eb;border-radius:8px;background:#f8fafc;font-size:12px;font-weight:600;cursor:pointer;height:34px}
        .stats-bar{flex:0 0 auto;padding:10px 20px;background:#f8fafc;border-bottom:1px solid #e5e7eb;display:flex;align-items:center;gap:6px;font-size:12px;color:#475569;flex-wrap:wrap}
        .stats-bar .sep{color:#e5e7eb}
        .stats-bar>button{display:inline-flex;align-items:center;gap:3px;padding:3px 8px;border:1px solid transparent;border-radius:999px;background:transparent;font:inherit;color:inherit;cursor:pointer;line-height:1.35;transition:background .15s,border-color .15s,color .15s}
        .stats-bar>button:hover{background:#eef2ff}
        .stats-bar>button:focus-visible{outline:none;border-color:#a5b4fc;background:#eef2ff}
        .stats-bar>button.active{background:#eef2ff;border-color:#c7d2fe;color:#1e293b}
        .stats-bar b{margin-left:0;color:#1e293b}.stats-bar .stat-available b{color:#059669}.stats-bar .stat-reserved b,.stats-bar .stat-nonac b{color:#d97706}.stats-bar .stat-occupied b{color:#dc2626}.stats-bar .stat-ac b{color:#2563eb}
        .seat-grid-wrap{flex:1 1 auto;min-height:0;overflow:auto;padding:16px 20px;background:#f8fafc;-webkit-overflow-scrolling:touch;overscroll-behavior:contain}
        .room-block{border:1px solid #e5e7eb;border-radius:10px;margin-bottom:16px;overflow:hidden}
        .room-head{position:sticky;top:0;z-index:1;padding:12px 16px;background:#fff;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;gap:8px}
        .room-head b{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .room-head .badge{flex:0 0 auto;background:#14b8a6;color:#fff;font-size:11px;padding:4px 10px;border-radius:12px;font-weight:600}
        .sec-title{display:flex;align-items:center;gap:7px;font-size:11px;font-weight:700;color:#334155;padding:14px 16px 8px;letter-spacing:.35px;text-transform:uppercase} .sec-title span:not(.section-mark){color:#64748b;font-weight:500}.section-mark{width:8px;height:8px;border-radius:50%;background:#0284c7}.nonac-mark{background:#7c3aed}
        .seats-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(90px,1fr));gap:10px;padding:0 16px 16px}
        .seat-box{min-height:62px;padding:9px 6px;border:1px solid #86efac;background:#ecfdf5;border-radius:10px;text-align:center;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;transition:transform .15s,box-shadow .15s,border-color .15s;touch-action:manipulation}
        .seat-box:hover,.seat-box:focus-visible{transform:translateY(-1px);border-color:#10b981;box-shadow:0 4px 12px rgba(34,197,94,.16);outline:none}
        .seat-box b{font-size:14px;color:#111827} .seat-box small{font-size:9px;color:#6b7280}
        .seat-box.occupied{border-color:#fecaca;background:#fef2f2;opacity:.75;cursor:not-allowed}
        .seat-box.reserved{border-color:#fcd34d;background:#fffbeb;opacity:.9;cursor:not-allowed}
        .divider{height:1px;background:#f1f5f9;margin:4px 0}
        .modal-foot{flex:0 0 auto;padding:10px 20px;border-top:1px solid #e5e7eb;display:flex;align-items:center;justify-content:flex-end;gap:12px;background:#fff;padding-bottom:max(10px,env(safe-area-inset-bottom))}
        .modal-foot button{min-height:40px;padding:9px 18px;border:1px solid #e5e7eb;border-radius:8px;background:#f8fafc;font-weight:600;cursor:pointer}
        @media(max-width:800px){
          .seat-modal-overlay{align-items:center;padding:8px}
          .seat-modal-container{width:100%;height:min(96dvh,900px);max-height:calc(100dvh - 16px);border-radius:14px}
          .seat-modal-header{padding:9px 12px;background:linear-gradient(110deg,#f8fafc,#eef2ff)}
          .seat-modal-title{font-size:16px;line-height:1.3}
          .seat-close{width:34px;height:34px;font-size:21px;border-radius:9px}
          .seat-modal-student{display:block;margin-top:2px;font-size:11px;font-weight:400}
          .seat-search-container{padding:7px 12px;border-bottom:none}
          .seat-search-field.has-value .seat-search-input{padding-right:36px}
          .seat-search-input{padding:8px 11px;font-size:12px;border-radius:9px}
          .seat-search-clear{right:6px;width:24px;height:24px;font-size:15px}
          .filters-shell{padding:0 12px 4px}
          .filters-toggle{display:flex;align-items:center;gap:7px;width:100%;min-height:32px;padding:5px 10px;border:1px solid #e5e7eb;border-radius:9px;background:#f8fafc;font-size:12px;font-weight:700;color:#334155;cursor:pointer}
          .filters-row{display:none;grid-template-columns:repeat(2,minmax(0,1fr));gap:5px 8px;padding:6px 0 1px}
          .filters-shell.open .filters-row{display:grid}
          .filters-row>*{min-width:0}
          .f-group label{font-size:9px;line-height:1.1;margin-bottom:1px;letter-spacing:.35px}
          .f-group .form-select-custom-trigger{min-height:30px;padding:4px 8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:11px}
          .assign-toggle{min-height:30px}
          .assign-toggle button{min-width:0;padding:4px 6px;font-size:10px;white-space:nowrap}
          .reset-btn{width:100%;height:30px;min-height:30px;padding:4px 8px;font-size:11px}
          .stats-bar{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:3px;padding:4px 12px 6px;overflow:visible;font-size:10px;border-bottom:1px solid #e5e7eb}
          .stats-bar .sep{display:none}
          .stats-bar>button{min-width:0;min-height:24px;justify-content:center;padding:2px 4px;border:1px solid #e5e7eb;background:#fff;font-size:10px;white-space:nowrap}
          .stats-bar>button.active{background:#eef2ff;border-color:#c7d2fe}
          .seat-grid-wrap{padding:6px 10px}
          .room-block{margin-bottom:7px;border-radius:9px}
          .room-head{padding:6px 10px}
          .room-head b{font-size:12px}
          .room-head .badge{font-size:10px;padding:3px 8px}
          .sec-title{padding:6px 10px 4px;font-size:10px;gap:6px}
          .section-mark{width:7px;height:7px}
          .seats-grid{grid-template-columns:repeat(auto-fill,minmax(48px,1fr));gap:5px;padding:0 7px 8px}
          .seat-box{min-height:46px;padding:5px 2px;border-radius:8px;gap:1px}
          .seat-box b{font-size:12px;line-height:1.15}
          .seat-box small{font-size:8px;line-height:1}
          .modal-foot{padding:6px 12px;padding-bottom:max(6px,env(safe-area-inset-bottom))}
          .modal-foot button{min-width:84px;min-height:36px;padding:7px 14px}
        }
        @media(max-width:640px){
          .seat-modal-overlay{padding:0}
          .seat-modal-container{width:100%;height:100dvh;max-height:100dvh;border-radius:0}
          .seat-modal-header{padding-top:max(9px,env(safe-area-inset-top))}
        }
        @media(max-width:360px){
          .seat-modal-header{padding:8px 10px}
          .seat-modal-title{font-size:15px}
          .seat-search-container{padding:6px 10px}
          .filters-shell{padding:0 10px 3px}
          .filters-row{gap:4px 6px;padding-top:5px}
          .f-group .form-select-custom-trigger{min-height:28px}
          .assign-toggle{min-height:28px}
          .reset-btn{height:28px;min-height:28px}
          .stats-bar{padding:3px 10px 5px;font-size:9px}
          .stats-bar>button{font-size:9px;min-height:23px;padding:2px 3px}
          .seat-grid-wrap{padding:5px 8px}
          .seats-grid{grid-template-columns:repeat(auto-fill,minmax(44px,1fr));gap:4px;padding:0 6px 7px}
          .seat-box{min-height:44px}
          .seat-box b{font-size:11px}
          .seat-box small{font-size:7px}
        }
      `}</style>
    </div>, document.body);
}
